/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */
import { html, LitElement } from 'lit-element';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { ControlStateMixin } from '../../utils/control-state-mixin.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { FocusBlurMixin } from '../../utils/focus-blur-mixin.js';
import { extendInteractiveArea } from '../../utils/interactive-area.js';
import { KEYCODE } from '../../utils/keycode.js';
import { windowLocation } from '../../utils/window-location.js';
import iconFont from './sh-icon.font.lit.scss.js';
import iconStyles from './sh-icon.lit.scss.js';

/**
 * @prop {String} icon - Defines the icon displayed. The value needs to correspond to one of the icons shown in the list on this page or a custom path in the format: url('/lorem-ipsum.png').
 * @prop {String} size - Defines the size of the icon. Accepted values are xs - 16px, s - 24px, m - 32px, l - 48px.
 * @prop {Boolean} button - If set to `true`, adds button behavior and styles to icon.
 * @prop {String} color - Defines the color of the icon. To see all color variables, check the Colors page.
 * @prop {String} badge - If present, a small dot will be shown on the top-right corner of the icon. A number can also be assigned to it (e.g. badge='25').
 * @prop {String} href - Hyperlink to navigate to on click.
 * @prop {Boolean} disabled - If set to `true` the color of the icon will change and no mouse/touch actions will be available
 */
export class SHIcon extends ControlStateMixin(FocusBlurMixin(LitElement)) {
  static get properties() {
    return {
      icon: { type: String, reflect: true },
      button: { type: Boolean, reflect: true },
      color: { type: String, reflect: true },
      size: { type: String, reflect: true },
      href: { type: String, reflect: true },
      badge: { type: String, reflect: true },
    };
  }

  static get styles() {
    return [iconFont, iconStyles, extendInteractiveArea(':host([button])')];
  }

  render() {
    return html`<div class="first"></div>
      <div class="second"></div>
      <div>
        ${this.badge !== undefined && this.badge !== null
          ? html` <sh-badge class="badge" label="${this.badge}"></sh-badge>`
          : ''}
      </div>`;
  }

  constructor() {
    super();
    this.size = 'm';
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('icon')) {
      this._updateIcon();
    }
    if (changedProperties.has('color')) {
      this._updateColor();
    }
    const listOfProperties = ['icon', 'button', 'color', 'size', 'href', 'badge'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  connectedCallback() {
    if (!this.button && !this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '-1');
    }
    super.connectedCallback();
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('no-hovermq');
    }
  }

  firstUpdated() {
    super.firstUpdated();
    this.addEventListener('click', this.clickEventListener);
    this.addEventListener('mousedown', function () {
      this._addActiveClass();
    });
    this.addEventListener('mouseup', function () {
      this._removeActiveClass();
    });
  }

  handleKeyup(e) {
    super.handleKeyup(e);

    if (e.keyCode === KEYCODE.ENTER) {
      this.click();
    }
  }

  _spaceKeyAction() {
    this.click();
  }

  clickEventListener(e) {
    if (this.href !== undefined) {
      windowLocation.util.setWindowLocationHRef(this.href);
    }
    if (this.disabled) {
      e.stopImmediatePropagation();
    }
  }

  _addActiveClass() {
    this.classList.add('active');
  }

  _removeActiveClass() {
    this.classList.remove('active');
  }

  _updateIcon() {
    if (this.icon && this.icon.indexOf('url') >= 0) {
      this.style.backgroundImage = this.icon;
    } else {
      this.style.backgroundImage = '';
    }
  }

  _updateColor() {
    if (this.color !== undefined) {
      this.style.color = this.color;
    }
  }
}

customElements.define('sh-icon', SHIcon);
